import { useQuery, useQueryClient } from 'react-query';
import createCachedSelector from 're-reselect';
import * as api from '../../services/api';
import useCRUD from '../useCRUD';
import { QUERY_CACHE_TIME } from '../../constants';
import useAccount from './useAccount';

const selector = createCachedSelector(
  (data) => data.data,
  (data) => {
    const branchGroups = data || [];
    const branchGroupsOptions = branchGroups.map(({ id, name }) => ({ value: id, label: name }));

    return { branchGroups, branchGroupsOptions };
  }
)({
  keySelector: (data, storeKey) => storeKey.join('#')
});

const useBranchGroups = () => {
  const { accountId } = useAccount();
  const queryClient = useQueryClient();

  const storeKey = ['branch-groups', accountId];
  const { isLoading, data } = useQuery(storeKey, () => api.fetchBranchGroups({ accountId }), {
    enabled: !!accountId,
    cacheTime: QUERY_CACHE_TIME,
    select: (branchGroupsData) => selector(branchGroupsData, storeKey)
  });

  const { addItem, editItem } = useCRUD(storeKey, {
    addApi: (branchGroup) => api.addBranchGroup({ accountId, branchGroup }),
    editApi: ({ id, ...branchGroup }) => api.updateBranchGroup({ accountId, branchGroupId: id, branchGroup })
  });

  const { deleteItem } = useCRUD(
    storeKey,
    { deleteApi: ({ id }) => api.deleteBranchGroup({ accountId, branchGroupId: id }) },
    {
      refetchOnSuccessDelete: true,
      onSuccess: () => queryClient.invalidateQueries(['branches', accountId])
    }
  );

  const { branchGroups = [], branchGroupsOptions = [] } = data || {};

  return {
    isLoading,
    branchGroups,
    branchGroupsOptions,
    addBranchGroup: addItem,
    editBranchGroup: editItem,
    deleteBranchGroup: deleteItem
  };
};

export default useBranchGroups;
